import { useLocation } from "react-router-dom";

/**
 * BrandWatermark — corner-pinned EchoSense monogram.
 *
 * Replaces the legacy full-screen yacht photo backdrop. Renders a small,
 * stately "ES" lettermark + wordmark in the bottom-right corner of every
 * authenticated page. Suppressed on:
 *   • The public landing page (`/`) — the hero yacht photo already does
 *     the visual work and a second watermark would compete.
 *   • The unsubscribe / book / forgot/reset auth pages — these have their
 *     own self-contained branded chrome.
 *
 * Rules:
 *   • pointer-events: none so it never blocks clicks
 *   • position: fixed bottom-right, low z-index so chrome sits on top
 *   • opacity 0.12 (light surfaces) / 0.18 (dark surfaces) — visible but not loud
 *   • respects prefers-reduced-motion (no animation at all)
 */
const SUPPRESS_PREFIXES = [
  "/",            // exact match handled separately
  "/about",
  "/disclaimer",
  "/privacy",
  "/sms-terms",
  "/book",
  "/forgot-password",
  "/reset-password",
];

const BrandWatermark = () => {
  const { pathname } = useLocation();

  // Suppress on landing (exact) + listed public pages
  if (pathname === "/" || SUPPRESS_PREFIXES.includes(pathname)) return null;

  // Light surfaces use a darker burgundy tone; dark surfaces use gold.
  const isLightSurface =
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/command-center") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/kyc") ||
    pathname.startsWith("/lifestyle") ||
    pathname.startsWith("/users") ||
    pathname.startsWith("/leads") ||
    pathname.startsWith("/funds") ||
    pathname.startsWith("/messages") ||
    pathname.startsWith("/payments") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/analytics") ||
    pathname.startsWith("/membership-fees") ||
    pathname.startsWith("/mailbox-setup") ||
    pathname.startsWith("/announcements") ||
    pathname.startsWith("/how-to-invest");

  const stroke   = isLightSurface ? "#8B1538" : "#EAB308";   // burgundy / gold
  const opacity  = isLightSurface ? 0.18      : 0.22;

  return (
    <div
      data-testid="brand-watermark"
      aria-hidden="true"
      style={{
        position:      "fixed",
        right:         "22px",
        bottom:        "22px",
        zIndex:        1,
        pointerEvents: "none",
        opacity,
      }}
    >
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Concentric circles — institutional crest */}
        <circle cx="60" cy="60" r="56" stroke={stroke} strokeWidth="0.8" fill="none" />
        <circle cx="60" cy="60" r="50" stroke={stroke} strokeWidth="0.4" fill="none" />

        {/* "ES" monogram — serif inspired */}
        <text
          x="60" y="62"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize="38"
          fontWeight="600"
          letterSpacing="2"
          fill={stroke}
        >
          ES
        </text>

        {/* Anchor mark below the monogram (small) */}
        <g
          transform="translate(60 88)"
          stroke={stroke}
          strokeWidth="1.1"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="0" cy="-6" r="2.2" />
          <line x1="0" y1="-4" x2="0" y2="6" />
          <line x1="-5" y1="-2" x2="5" y2="-2" />
          <path d="M -7 4 Q 0 10 7 4" />
        </g>

        {/* Wordmark — full ring text */}
        <defs>
          <path
            id="bw-ring"
            d="M 60 60 m -44 0 a 44 44 0 1 1 88 0 a 44 44 0 1 1 -88 0"
          />
        </defs>
        <text
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize="6.4"
          letterSpacing="3"
          fill={stroke}
        >
          <textPath href="#bw-ring" startOffset="22%">
            ECHOSENSE  ·  CAPITAL  ·  MARKETS  ·  ESTD  2026
          </textPath>
        </text>
      </svg>
    </div>
  );
};

export default BrandWatermark;
